// Refactored FiltersContainer to use Ant Design instead of custom accordion components

import React, { useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse, ConfigProvider, Flex } from '@app/ui';
import '../styles/Filters.scss';
import '@app/components/filters/Filters.scss';
import SelectedFiltersTags from '@app/components/filters/components/SelectedFiltersTags';
import useAppLayoutHeaderBottom, {
    getAppLayoutHeaderBottom,
} from '@app/components/hooks/useAppLayoutHeaderBottom';

export type FilterSelectionType = {
    title: string;
    selections: string[];
    displayValues?: string[];
};

type FilterContainerProps = {
    selections: FilterSelectionType[];
    children: React.ReactNode;
};

const FiltersContainer = ({
    selections = [],
    children,
}: FilterContainerProps) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const stickyRef = useRef<HTMLDivElement | null>(null);
    const layoutHeaderBottom = useAppLayoutHeaderBottom();
    const lastStuckClassRef = useRef<boolean | null>(null);

    // Same contract as ReFilters: CSS vars on <html> so StickySocialPostsTableLayout
    // (and SocialPosts.scss) can place the table header under the stuck filter strip.
    useLayoutEffect(() => {
        const root = document;
        const STUCK_CLASS = 'filters-sticky-active';

        const syncStuckClass = () => {
            const el = stickyRef.current as HTMLElement | null;
            if (!el) return;

            const headerBottom = getAppLayoutHeaderBottom();
            document.documentElement.style.setProperty(
                '--app-header-bottom',
                `${headerBottom}px`,
            );

            const top = el.getBoundingClientRect().top;
            const stuck = top <= headerBottom + 0.5;
            const filtersHeight = el.offsetHeight;

            if (lastStuckClassRef.current !== stuck) {
                lastStuckClassRef.current = stuck;
                el.classList.toggle(STUCK_CLASS, stuck);
            }

            document.documentElement.style.setProperty(
                '--filters-sticky-offset',
                stuck ? `${filtersHeight + 12}px` : '0px',
            );
            document.documentElement.style.setProperty(
                '--filters-sticky-gap-top',
                stuck ? `${headerBottom + filtersHeight}px` : '0px',
            );
            document.documentElement.style.setProperty(
                '--filters-sticky-gap-visible',
                stuck ? '12px' : '0px',
            );
        };

        syncStuckClass();

        root.addEventListener('scroll', syncStuckClass, {
            passive: true,
            capture: true,
        });
        window.addEventListener('resize', syncStuckClass);

        const resizeObserver = new ResizeObserver(syncStuckClass);
        const observeTarget = () => {
            const el = stickyRef.current;
            if (el) resizeObserver.observe(el);
        };
        observeTarget();
        const observeRaf = requestAnimationFrame(observeTarget);

        return () => {
            cancelAnimationFrame(observeRaf);
            (stickyRef.current as HTMLElement | null)?.classList.remove(
                STUCK_CLASS,
            );
            lastStuckClassRef.current = null;
            root.removeEventListener('scroll', syncStuckClass, true);
            window.removeEventListener('resize', syncStuckClass);
            resizeObserver.disconnect();
            const html = document.documentElement.style;
            html.removeProperty('--filters-sticky-offset');
            html.removeProperty('--filters-sticky-gap-top');
            html.removeProperty('--filters-sticky-gap-visible');
        };
    }, [layoutHeaderBottom]);

    const titleContent = (
        <Flex>
            <Flex
                gap={12}
                justify="center"
                align="center"
                className="-m-r-2"
                style={{
                    height: 'fit-content',
                    fontSize: 16,
                }}
            >
                <i className="fa-regular fa-sliders" />
                {t('filters.title')}
            </Flex>
            <Flex
                style={{
                    display: 'flex',
                    flex: 1,
                }}
            >
                <SelectedFiltersTags
                    selectedFiltersArray={selections.map(selection => ({
                        title: selection.title,
                        elements: selection.selections,
                    }))}
                />
            </Flex>
        </Flex>
    );

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: '#FAFAFA',
                        colorBorder: '#BFBFBF',
                    },
                },
            }}
        >
            <Collapse
                className="sticky filters-sticky-layout-padded"
                ref={stickyRef}
                style={{
                    top: layoutHeaderBottom,
                    position: 'sticky',
                    overflow: 'hidden',
                }}
                activeKey={isExpanded ? ['1'] : []}
                onChange={() => setIsExpanded(!isExpanded)}
                expandIconPosition="end"
                items={[
                    {
                        key: '1',
                        label: titleContent,
                        children: (
                            <div className="filters-accordion__selects">
                                {children}
                            </div>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

export default FiltersContainer;
